import type { NotificationItem } from './api-client';

/**
 * Format a price-alert's charter week for display, INCLUDING the year.
 * Alert weeks routinely point at the next season (e.g. a March 2027 week
 * surfaced in mid-2026), so a month/day-only label like "Mar 27 → Apr 3"
 * reads as a past date. The year on the end bound disambiguates it.
 *
 * Date-only strings parse as UTC midnight, so format in UTC too — otherwise a
 * user in UTC-X sees the date shift back by a day.
 */
export function formatNotificationWeek(
  dateFrom: string | undefined,
  dateTo: string | undefined
): string | null {
  if (!dateFrom || !dateTo) return null;
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${dateFrom} → ${dateTo}`;
  }
  const base: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const withYear: Intl.DateTimeFormatOptions = { ...base, year: 'numeric' };
  return `${from.toLocaleDateString(undefined, base)} → ${to.toLocaleDateString(undefined, withYear)}`;
}

export type NotificationRuleType =
  | 'price_drop'
  | 'price_increase'
  | 'price_change'
  | 'new_boat_in_comp_set';

export interface PriceAlertIntelligence {
  rank: { previous: number; current: number; total: number };
  userBoats: Array<{
    name: string;
    pid: string;
    price: string;
    vsCompetitorPct: number;
  }>;
  userMedianPrice: string | null;
}

/** One week of a price alert: when it starts and ends, and how far its price moved. */
export interface PriceAlertWeek {
  dateFrom: string;
  dateTo: string;
  changePct: number;
}

export interface NotificationPayload {
  ruleType: NotificationRuleType | (string & {});
  message: string;
  compSetPid?: string | null;
  compSetName?: string | null;
  marketBoatName?: string;
  marketBoatPid?: string;
  charterName?: string | null;
  oldPrice?: string;
  newPrice?: string;
  changePct?: number;
  currency?: string;
  dateFrom?: string;
  dateTo?: string;
  fetchedAt?: string | null;
  intelligence?: PriceAlertIntelligence | null;
  /**
   * Every week of the boat that moved in the run, the headline (the fields
   * above) first. Empty for alerts from before multi-week alerts (#115).
   */
  weeks: PriceAlertWeek[];
}

export function extractNotificationPayload(notification: NotificationItem): NotificationPayload {
  const payload = notification.payload;
  const changePctRaw = payload?.['changePct'];
  const changePct =
    typeof changePctRaw === 'number'
      ? changePctRaw
      : typeof changePctRaw === 'string'
        ? Number(changePctRaw)
        : undefined;

  return {
    ruleType: (payload?.['ruleType'] as string) ?? '',
    message: (payload?.['message'] as string) ?? 'Notification',
    compSetPid: payload?.['compSetPid'] as string | null | undefined,
    compSetName: payload?.['compSetName'] as string | null | undefined,
    marketBoatName: payload?.['marketBoatName'] as string | undefined,
    marketBoatPid: payload?.['marketBoatPid'] as string | undefined,
    charterName: payload?.['charterName'] as string | null | undefined,
    oldPrice: payload?.['oldPrice'] as string | undefined,
    newPrice: payload?.['newPrice'] as string | undefined,
    changePct: typeof changePct === 'number' && Number.isFinite(changePct) ? changePct : undefined,
    currency: payload?.['currency'] as string | undefined,
    dateFrom: payload?.['dateFrom'] as string | undefined,
    dateTo: payload?.['dateTo'] as string | undefined,
    fetchedAt: payload?.['fetchedAt'] as string | null | undefined,
    intelligence: extractIntelligence(payload?.['intelligence']),
    weeks: extractWeeks(payload?.['weeks']),
  };
}

function extractWeeks(raw: unknown): PriceAlertWeek[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== 'object') return [];
    const w = entry as Record<string, unknown>;
    const { dateFrom, dateTo, changePct } = w;
    if (typeof dateFrom !== 'string' || typeof dateTo !== 'string') return [];
    if (typeof changePct !== 'number' || !Number.isFinite(changePct)) return [];
    return [{ dateFrom, dateTo, changePct }];
  });
}

/**
 * The weeks of a price alert besides its headline: the first few to show, in
 * the alert's order (biggest move first), and how many more there are. Null
 * when the alert is about one week.
 */
export function otherWeeks(
  payload: NotificationPayload,
  show = 3
): { shown: PriceAlertWeek[]; more: number; total: number } | null {
  const others = payload.weeks.slice(1);
  if (others.length === 0) return null;
  return {
    shown: others.slice(0, show),
    more: Math.max(0, others.length - show),
    total: others.length,
  };
}

/** A week's start as "Aug 8". Date-only strings parse as UTC midnight, so format in UTC. */
export function formatWeekStart(dateFrom: string): string {
  const d = new Date(dateFrom);
  if (Number.isNaN(d.getTime())) return dateFrom;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function extractIntelligence(raw: unknown): PriceAlertIntelligence | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const rank = obj['rank'] as Record<string, unknown> | undefined;
  const userBoats = obj['userBoats'];
  if (!rank || !Array.isArray(userBoats)) return null;
  return {
    rank: {
      previous: Number(rank['previous'] ?? 0),
      current: Number(rank['current'] ?? 0),
      total: Number(rank['total'] ?? 0),
    },
    userBoats: userBoats
      .filter((b): b is Record<string, unknown> => b !== null && typeof b === 'object')
      .map((b) => ({
        name: String(b['name'] ?? ''),
        pid: String(b['pid'] ?? ''),
        price: String(b['price'] ?? ''),
        vsCompetitorPct: Number(b['vsCompetitorPct'] ?? 0),
      })),
    userMedianPrice: (obj['userMedianPrice'] as string | null | undefined) ?? null,
  };
}

export const NOTIFICATION_LABELS: Record<string, string> = {
  price_drop: 'Price Drop',
  price_increase: 'Price Increase',
  price_change: 'Price Change',
  new_boat_in_comp_set: 'New Boats',
};

export const NOTIFICATION_COLORS: Record<string, string> = {
  price_drop: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  price_increase: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  price_change: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  new_boat_in_comp_set: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};
